'use client';

import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import { getTermsAndConditions } from '@/src/lib/sanity.queries';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface TermsAndConditionsDialogProps {
  locale: string;
  children: React.ReactNode;
}

interface TermsAndConditionsData {
  title: string;
  doc1: string;
  doc2: string;
  doc3: string;
  doc4: string;
  lastUpdated: string;
  version: string;
}

export default function TermsAndConditionsDialog({
  locale,
  children,
}: TermsAndConditionsDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const t = useTranslations('Home.TermsAndConditions');
  const [termsData, setTermsData] = useState<TermsAndConditionsData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open && !termsData) {
      fetchTermsData();
    }
  };

  const fetchTermsData = async () => {
    setIsLoading(true);
    try {
      const data = await getTermsAndConditions(locale);
      setTermsData(data);
    } catch (error) {
      console.error('Error loading terms content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getContent = () => {
    // First try to get content from Sanity
    if (termsData) {
      const sanityContent = [termsData.doc1, termsData.doc2, termsData.doc3, termsData.doc4].filter(
        Boolean
      );
      if (sanityContent.length > 0) {
        return sanityContent;
      }
    }

    // Fallback to translations if Sanity data is not available
    try {
      return [t('doc1'), t('doc2'), t('doc3'), t('doc4')].filter(Boolean);
    } catch (error) {
      console.error('Error loading translation content:', error);
      return [];
    }
  };

  const contentParagraphs = getContent();

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-h-[90%] max-w-[90%] overflow-y-auto rounded-3xl bg-white text-center">
        <DialogHeader>
          <DialogTitle className="text-center text-4xl font-bold text-gray-900">
            {termsData?.title || t('Title')}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-6 px-4">
          <div className="prose prose-lg max-w-none">
            {isLoading ? (
              <div className="py-8 text-center text-gray-500">
                {locale === 'ar' ? 'جاري التحميل...' : 'Loading...'}
              </div>
            ) : (
              <div className="space-y-4 leading-relaxed text-gray-700">
                {contentParagraphs.length > 0 ? (
                  contentParagraphs.map((paragraph: string, index: number) => (
                    <p key={index} className="text-xl leading-6">
                      {paragraph}
                    </p>
                  ))
                ) : (
                  <div className="py-8 text-center text-gray-500">
                    {locale === 'ar'
                      ? 'لا توجد شروط وأحكام متاحة حالياً'
                      : 'No terms and conditions available at the moment'}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
